// File: ./src/Footer.js
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from './LangContext';

function Footer() {
  const location = useLocation();
  const { lang, setLang } = useLang();
  const [visible, setVisible] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langMenuRef = useRef(null);
  const langButtonRef = useRef(null);

  useEffect(() => {
    // If we are on the Timer Page ("/"), use the existing scroll logic
    if (location.pathname === '/') {
      const handleScroll = () => {
        setVisible(window.scrollY > 50);
      };

      setVisible(window.scrollY > 50);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }

    // For all other pages, we want the footer pinned at the bottom
    setVisible(true);
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (!langOpen) return undefined;

    const handlePointerDown = (event) => {
      if (langMenuRef.current?.contains(event.target) || langButtonRef.current?.contains(event.target)) {
        return;
      }
      setLangOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLangOpen(false);
        langButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [langOpen]);

  const applyLanguage = (nextLang) => {
    setLang(nextLang);
    localStorage.setItem('appLang', nextLang);
    setLangOpen(false);
    langButtonRef.current?.focus();
  };

  // If we're on "/", keep the old dynamic class logic
  // Otherwise, apply a pinned class
  const footerClass = location.pathname === '/'
    ? `footer ${visible ? 'visible' : 'hidden'}`
    : 'footer footer-pinned';

  return (
    <footer className={footerClass}>
      <div className="footer-nav">
        <Link to="/">{lang === 'de' ? 'Start' : 'Home'}</Link>
        <Link to="/about">{lang === 'de' ? 'Über' : 'About'}</Link>
        <Link to="/imprint">{lang === 'de' ? 'Impressum' : 'Imprint'}</Link>
        <Link to="/contact">{lang === 'de' ? 'Kontakt' : 'Contact'}</Link>
        <Link to="/privacy">{lang === 'de' ? 'Datenschutz' : 'Privacy'}</Link>

        <div className="footer-lang">
          <button
            ref={langButtonRef}
            type="button"
            className={`footer-lang-trigger${langOpen ? ' is-open' : ''}`}
            aria-haspopup="menu"
            aria-expanded={langOpen}
            aria-controls="footer-language-menu"
            onClick={() => setLangOpen((open) => !open)}
          >
            <span className="footer-lang-trigger-label">{lang === 'de' ? 'Sprache' : 'Language'}</span>
            <span className="footer-lang-trigger-value">{lang === 'de' ? 'Deutsch' : 'English'}</span>
            <span className="footer-lang-trigger-caret" aria-hidden="true">▾</span>
          </button>

          {langOpen && (
            <div
              id="footer-language-menu"
              ref={langMenuRef}
              className="footer-lang-menu"
              role="menu"
              aria-label={lang === 'de' ? 'Sprachauswahl' : 'Language selection'}
            >
              <p className="footer-lang-menu-label">{lang === 'de' ? 'Sprache wählen' : 'Choose language'}</p>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={lang === 'en'}
                className={`footer-lang-option${lang === 'en' ? ' is-active' : ''}`}
                onClick={() => applyLanguage('en')}
              >
                <span>English</span>
                {lang === 'en' && <span className="footer-lang-option-check" aria-hidden="true">✓</span>}
              </button>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={lang === 'de'}
                className={`footer-lang-option${lang === 'de' ? ' is-active' : ''}`}
                onClick={() => applyLanguage('de')}
              >
                <span>Deutsch</span>
                {lang === 'de' && <span className="footer-lang-option-check" aria-hidden="true">✓</span>}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="footer-youtube">
        <a
          className="youtube-button"
          href="https://www.youtube.com/channel/UC8E1eJvIbv82zyEMmBxDyOQ"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src={`${process.env.PUBLIC_URL}/yt_icon_rgb.png`}
            alt="YouTube"
            style={{ minHeight: '20px' }}
          />
        </a>
      </div>
      {/* Cookie Declaration wird auf der Privacy-Seite angezeigt */}
    </footer>
  );
}

export default Footer;
